import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import { useCompanyCore } from '../../../../core/modules/company';
import { stockComparisonService, StockComparisonConfig } from '../../../../core/modules/stockComparison/services/api';

// Esta pestaña SOLO tiene el interruptor general del modulo -- a diferencia
// del chatbot y Toma de Inventario, el umbral y el nombre del sistema
// secundario NO viven aca: Stock Comparison necesita que company_admin y
// Usuario tambien puedan configurarlo/subir el archivo, y esta pantalla
// (Company.tsx) es superadmin-only, asi que esa parte vive en una pantalla
// aparte que ven los tres roles. Pedido explicito de Seba: el
// activar/desactivar si se queda aca, junto con el resto de los modulos.
const StockComparisonTab = () => {
  const { company } = useCompanyCore();
  const companyId = company?.id ? Number(company.id) : null;

  const [config, setConfig] = useState<StockComparisonConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [savingEnabled, setSavingEnabled] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await stockComparisonService.getConfig(companyId);
      setConfig(data);
    } catch (err) {
      setError('No se pudo cargar la configuración de Stock Comparison.');
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleToggleEnabled = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!companyId) return;
    const nextValue = event.target.checked;
    setSavingEnabled(true);
    setError(null);
    setInfo(null);
    try {
      const result = await stockComparisonService.updateConfig(companyId, { stockComparisonEnabled: nextValue });
      setConfig(result);
      setInfo(
        nextValue
          ? 'Stock Comparison habilitado para esta empresa.'
          : 'Stock Comparison deshabilitado para esta empresa.'
      );
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'No se pudo cambiar el estado del módulo.');
    } finally {
      setSavingEnabled(false);
    }
  };

  if (loading) {
    return (
      <Box className="flex items-center justify-center p-8">
        <Card className="w-full max-w-2xl">
          <CardContent>
            <Skeleton variant="rectangular" height={140} />
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="flex items-center justify-center p-4 sm:p-8">
      <Card className="w-full max-w-2xl shadow-xl">
        <CardContent className="p-6 sm:p-8">
          <Stack spacing={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <CompareArrowsIcon color="action" fontSize="small" />
              <Typography variant="h5" component="h2" className="font-bold">
                Stock Comparison
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              Compara a diario el stock del sistema primario contra el de un sistema secundario
              (ej. el ERP del cliente). El umbral de desviación, el nombre del sistema secundario y
              la carga del archivo se configuran en una pantalla aparte, disponible para
              administradores de empresa y usuarios -- acá solo se activa o desactiva el módulo
              para la empresa.
            </Typography>

            <Box display="flex" alignItems="center" gap={1}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!config?.stockComparisonEnabled}
                    onChange={handleToggleEnabled}
                    disabled={savingEnabled}
                  />
                }
                label={
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    color={config?.stockComparisonEnabled ? 'success.main' : 'text.secondary'}
                  >
                    {config?.stockComparisonEnabled
                      ? 'Stock Comparison habilitado para esta empresa'
                      : 'Stock Comparison deshabilitado para esta empresa'}
                  </Typography>
                }
              />
              {savingEnabled && <CircularProgress size={16} />}
            </Box>

            {error && (
              <Alert severity="error" onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
            {info && (
              <Alert severity="success" onClose={() => setInfo(null)}>
                {info}
              </Alert>
            )}
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default StockComparisonTab;
